import { useState } from "react"
import { Layers, Wifi, Code2, ScanSearch, ShieldCheck } from "lucide-react"
import { motion, useReducedMotion } from "motion/react"
import { colors, accent } from '@/lib/tokens'

const features = [
  {
    tab: "Attribution",
    icon: Layers,
    title: "Cost attribution by provider",
    description:
      "Every API call is tagged to its provider, model, and file, then aggregated in real time. Track spending across OpenAI, Anthropic, Google, and more with no delayed billing surprises.",
  },
  {
    tab: "Static scan",
    icon: ScanSearch,
    title: "AST-powered static analysis",
    description:
      "Web-Tree-Sitter parses your codebase to map every AI call across imports, aliases, and wrappers. Complete coverage with zero runtime overhead.",
  },
  {
    tab: "Telemetry",
    icon: Wifi,
    title: "Real-time telemetry",
    description:
      "Stream cost events live for dashboards, or batch-collect via HTTPS for ingestion pipelines. Both modes from a single SDK with one line of setup.",
  },
  {
    tab: "SDK & IDE",
    icon: Code2,
    title: "VS Code extension & Python SDK",
    description:
      "See cost estimates inline as you write code, before anything ships. The Python SDK auto-intercepts requests, httpx, aiohttp, FastAPI, and Flask with zero configuration.",
  },
  {
    tab: "Suggestions",
    icon: ShieldCheck,
    title: "Optimization suggestions",
    description:
      "Automatically flagged: oversized context windows, redundant calls, cheaper model alternatives. Ranked by estimated monthly savings with file and line attribution.",
  },
]

// Mobile-only: chip picker at the top, single expanded card below.
// Hidden ≥720px where the desktop card grid takes over.
function FeaturesMobile({ animated }: { animated: boolean }) {
  const [active, setActive] = useState(0)
  const f = features[active]
  const Icon = f.icon

  const card = (
    <article
      style={{
        background: colors.bgBase,
        border: `1px solid ${colors.borderDefault}`,
        borderRadius: 12,
        padding: '18px 18px 20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 16,
        boxShadow: '0 6px 20px rgba(15,23,42,0.04)',
        minHeight: 230,
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          width: 36,
          height: 36,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 10,
          background: colors.bgSubtle,
          color: '#14b8a6',
          flexShrink: 0,
        }}
      >
        <Icon size={20} />
      </div>
      <h3
        style={{
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: colors.textPrimary,
          margin: 0,
          lineHeight: 1.18,
        }}
      >
        {f.title}
      </h3>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.62,
          color: colors.textSecondary,
          margin: 0,
        }}
      >
        {f.description}
      </p>
    </article>
  )

  return (
    <div
      className="features-mobile"
      style={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        flexDirection: 'column',
        gap: 'clamp(28px, 4.5vw, 64px)',
      }}
    >
      {/* Chip picker. 3 on top, 2 below. */}
      <div
        role="tablist"
        aria-label="Features"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 'clamp(10px, 2vw, 18px)',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {features.map((feat, i) => {
          const isActive = active === i
          const gridColumn =
            i === 3 ? '2 / span 2' : i === 4 ? '4 / span 2' : 'span 2'
          return (
            <button
              key={feat.tab}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(i)}
              style={{
                gridColumn,
                padding: '8px 13px',
                fontSize: 12.5,
                fontWeight: isActive ? 600 : 500,
                borderRadius: 6,
                border: `1px solid ${isActive ? colors.accentBorder : colors.borderDefault}`,
                background: isActive ? colors.accentSubtle : colors.bgBase,
                color: isActive ? accent : colors.textSecondary,
                cursor: 'pointer',
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                whiteSpace: 'nowrap',
                letterSpacing: '-0.01em',
              }}
            >
              {feat.tab}
            </button>
          )
        })}
      </div>

      {animated ? (
        <motion.div
          key={f.title}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        >
          {card}
        </motion.div>
      ) : (
        card
      )}
    </div>
  )
}

function FeatureCard({
  icon: Icon,
  title,
  description,
  tall = false,
}: {
  icon: typeof Layers
  title: string
  description: string
  tall?: boolean
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -3 }}
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 18,
        border: `1px solid ${colors.borderDefault}`,
        background: colors.bgBase,
        boxShadow: "0 10px 28px rgba(15,23,42,0.04)",
        padding: "24px 24px 22px",
        minHeight: tall ? 220 : 190,
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: "linear-gradient(180deg, rgba(250,250,249,0.88), rgba(255,255,255,0))",
        }}
      />
      <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 18 }}>
        <div
          style={{
            display: "inline-flex",
            width: 40,
            height: 40,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 12,
            background: colors.bgSubtle,
            color: "#14b8a6",
            flexShrink: 0,
            transition: "transform 0.2s ease, background 0.2s ease",
          }}
        >
          <Icon size={24} />
        </div>
        <div>
          <h3
            style={{
              fontSize: "clamp(18px, 2.2vw, 24px)",
              fontWeight: 700,
              letterSpacing: "-0.04em",
              color: colors.textPrimary,
              margin: "0 0 10px",
              lineHeight: 1.05,
            }}
          >
            {title}
          </h3>
          <p style={{ fontSize: 14, lineHeight: 1.68, color: "#5f5b56", margin: 0 }}>
            {description}
          </p>
        </div>
      </div>
    </motion.article>
  )
}

export function FeaturesSection() {
  const shouldReduceMotion = useReducedMotion()

  if (shouldReduceMotion) {
    return (
      <section
        id="features"
        style={{
          position: "relative",
          borderTop: `1px solid ${colors.borderDefault}`,
          backgroundColor: colors.bgPage,
          padding: "clamp(80px, 10vw, 140px) 20px",
        }}
      >
        <div className="dot-grid" style={{ position: "absolute", inset: 0, opacity: 0.45, pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: 1360, margin: "0 auto", display: "flex", flexDirection: "column", gap: "clamp(28px, 4.5vw, 64px)" }}>
          <div style={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: "clamp(28px, 4.5vw, 64px)" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: accent, margin: 0 }}>
                Built for developers
              </p>
              <h2 style={{ fontSize: "clamp(30px, 3.5vw, 42px)", fontWeight: 700, letterSpacing: "-0.03em", color: colors.textPrimary, lineHeight: 1.04, margin: 0 }}>
                A sharper toolkit for understanding API spend
              </h2>
            </div>
            <p style={{ fontSize: 16, lineHeight: 2, color: colors.textSecondary, margin: 0 }}>
              Recost gives you cost visibility from runtime telemetry, static analysis, and provider-level attribution.
            </p>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 20 }}
            className="features-grid"
          >
            {features.map((f, i) => (
              <FeatureCard
                key={f.title}
                icon={f.icon}
                title={f.title}
                description={f.description}
                tall={i === 0 || i === 3}
              />
            ))}
          </div>

          {/* Mobile: same chip picker, motion disabled */}
          <FeaturesMobile animated={false} />
        </div>

        <style>{`
          .features-mobile { display: none; }
          @media (max-width: 720px) {
            .features-grid { display: none !important; }
            .features-mobile { display: flex; }
          }
        `}</style>
      </section>
    )
  }

  return (
    <section
      id="features"
      style={{
        position: "relative",
        borderTop: `1px solid ${colors.borderDefault}`,
        backgroundColor: colors.bgPage,
        padding: "clamp(80px, 10vw, 140px) 20px",
        overflow: "hidden",
      }}
    >
      <div className="dot-grid" style={{ position: "absolute", inset: 0, opacity: 0.45, pointerEvents: "none" }} />
      <div
        style={{
          position: "relative",
          maxWidth: 1400,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "minmax(340px, 460px) minmax(0, 1fr)",
          gap: "clamp(48px, 6vw, 80px)",
          alignItems: "start",
        }}
        className="features-split"
      >
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: "sticky",
            top: 100,
            alignSelf: "start",
            paddingRight: 8,
            display: "flex",
            flexDirection: "column",
            gap: "clamp(28px, 4.5vw, 64px)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: accent,
                margin: 0,
              }}
            >
              Built for developers
            </p>
            <h2
              style={{
                fontSize: "clamp(30px, 4vw, 46px)",
                fontWeight: 700,
                letterSpacing: "-0.04em",
                lineHeight: 1.02,
                color: colors.textPrimary,
                margin: 0,
              }}
            >
              A sharper toolkit for understanding API spend.
            </h2>
          </div>
          <p style={{ fontSize: 16, lineHeight: 2, color: colors.textSecondary, margin: 0, maxWidth: 440 }}>
            One-line SDK install, AST scanning, and streaming telemetry. Every dollar traced back to the exact line of code that spent it.
          </p>
        </motion.div>

        {/* Desktop + tablet: card grid (≥720px) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 24, borderLeft: `1px solid ${colors.borderDefault}`, paddingLeft: 48 }} className="features-grid">
          <FeatureCard
            icon={features[0].icon}
            title={features[0].title}
            description={features[0].description}
            tall
          />
          <FeatureCard
            icon={features[1].icon}
            title={features[1].title}
            description={features[1].description}
          />
          <FeatureCard
            icon={features[2].icon}
            title={features[2].title}
            description={features[2].description}
            tall
          />
          <FeatureCard
            icon={features[3].icon}
            title={features[3].title}
            description={features[3].description}
          />
          <div style={{ gridColumn: "1 / -1" }}>
            <FeatureCard
              icon={features[4].icon}
              title={features[4].title}
              description={features[4].description}
            />
          </div>
        </div>

        {/* Mobile: chip picker + single expanded card (hidden ≥720px) */}
        <FeaturesMobile animated />
      </div>

      <style>{`
        .features-mobile { display: none; }

        @media (max-width: 980px) {
          .features-split {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .features-grid {
            border-left: none !important;
            padding-left: 0 !important;
          }

          .features-split > :first-child {
            position: relative !important;
            top: auto !important;
            padding-right: 0 !important;
          }
        }

        @media (max-width: 768px) {
          #features {
            padding: 64px 20px !important;
          }
        }

        @media (max-width: 720px) {
          .features-grid { display: none !important; }
          .features-mobile { display: flex; }
          .features-split {
            gap: clamp(28px, 4.5vw, 64px) !important;
          }
        }
      `}</style>
    </section>
  )
}
